import type { ApiPage, ProblemDetail } from '@ddone/shared-types';

export class ApiError extends Error {
  readonly problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  version?: number;
  idempotencyKey?: string;
}

export interface ListParams {
  page?: number;
  size?: number;
  sort?: string;
  query?: string;
  filters?: Record<string, string | number | boolean | undefined>;
}

export interface ApiRuntimeConfiguration {
  baseUrl?: string;
  accessToken?: () => Promise<string | undefined>;
  logout?: () => Promise<void>;
}

let runtimeConfiguration: ApiRuntimeConfiguration = {};

export function configureApiRuntime(configuration: ApiRuntimeConfiguration): void {
  runtimeConfiguration = { ...configuration };
}

export async function runApiRuntimeLogout(): Promise<boolean> {
  if (!runtimeConfiguration.logout) return false;
  await runtimeConfiguration.logout();
  return true;
}

const unsafeMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : undefined;
}

function createProblem(status: number, statusText: string): ProblemDetail {
  return {
    title: statusText || 'Errore inatteso',
    status,
    detail: 'Il server non ha restituito dettagli.'
  };
}

export class ApiClient {
  constructor(private readonly baseUrl = '/api/v1') {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, version, idempotencyKey, ...requestInit } = options;
    const method = requestInit.method?.toUpperCase() ?? 'GET';
    const headers = new Headers(requestInit.headers);
    headers.set('Accept', 'application/json');
    headers.set('X-Requested-With', 'DDone-Web');
    if (body !== undefined) headers.set('Content-Type', 'application/json');
    if (unsafeMethods.has(method)) {
      const csrf = readCookie('DDONE-XSRF-TOKEN') ?? readCookie('XSRF-TOKEN');
      if (csrf) headers.set('X-XSRF-TOKEN', csrf);
    }
    if (version !== undefined) headers.set('If-Match', `"${version}"`);
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
    const accessToken = await runtimeConfiguration.accessToken?.();
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

    let response: Response;
    try {
      const init: RequestInit = {
        ...requestInit,
        method,
        headers,
        credentials: 'include'
      };
      if (body !== undefined) init.body = JSON.stringify(body);
      response = await fetch(`${runtimeConfiguration.baseUrl ?? this.baseUrl}${path}`, init);
    } catch (cause) {
      throw new ApiError({
        title: 'Server non raggiungibile',
        status: 0,
        detail: cause instanceof Error ? cause.message : 'Errore di rete'
      });
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type') ?? '';
      const problem = contentType.includes('json')
        ? ((await response.json()) as ProblemDetail)
        : createProblem(response.status, response.statusText);
      throw new ApiError({ ...problem, status: response.status });
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async list<T>(path: string, params: ListParams = {}): Promise<ApiPage<T>> {
    const search = new URLSearchParams();
    if (params.page !== undefined) search.set('page', String(params.page));
    if (params.size !== undefined) search.set('size', String(params.size));
    if (params.sort) {
      const [sort, direction] = params.sort.split(',');
      if (sort) search.set('sort', sort);
      if (direction) search.set('direction', direction);
    }
    if (params.query) search.set('search', params.query);
    Object.entries(params.filters ?? {}).forEach(([key, value]) => {
      if (value !== undefined && value !== '') search.set(key, String(value));
    });
    const query = search.size ? `?${search.toString()}` : '';
    const page = await this.request<ApiPage<T> | WirePage<T> | T[]>(`${path}${query}`);
    if (Array.isArray(page)) {
      return {
        content: page,
        page: 0,
        size: page.length,
        totalElements: page.length,
        totalPages: page.length ? 1 : 0
      };
    }
    if ('content' in page) return page;
    const totalPages = page.size > 0 ? Math.ceil(page.total / page.size) : 0;
    return {
      content: page.items,
      page: page.page,
      size: page.size,
      totalElements: page.total,
      totalPages
    };
  }

  create<T, TBody>(path: string, body: TBody): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body,
      idempotencyKey: crypto.randomUUID()
    });
  }

  update<T, TBody>(path: string, body: TBody, version: number): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body, version });
  }

  patch<T, TBody>(path: string, body: TBody, version: number): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body, version });
  }

  remove(path: string, version?: number): Promise<void> {
    return this.request<void>(path, {
      method: 'DELETE',
      ...(version === undefined ? {} : { version })
    });
  }

  async upload<T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> {
    const body = new FormData();
    body.set('file', file, file.name);
    Object.entries(fields).forEach(([key, value]) => body.set(key, value));
    const headers = new Headers({ Accept: 'application/json', 'X-Requested-With': 'DDone-Web' });
    const csrf = readCookie('DDONE-XSRF-TOKEN') ?? readCookie('XSRF-TOKEN');
    if (csrf) headers.set('X-XSRF-TOKEN', csrf);
    const accessToken = await runtimeConfiguration.accessToken?.();
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    let response: Response;
    try {
      response = await fetch(`${runtimeConfiguration.baseUrl ?? this.baseUrl}${path}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body
      });
    } catch (cause) {
      throw new ApiError({
        title: 'Upload non riuscito',
        status: 0,
        detail: cause instanceof Error ? cause.message : 'Errore di rete'
      });
    }
    if (!response.ok) {
      const problem = response.headers.get('content-type')?.includes('json')
        ? ((await response.json()) as ProblemDetail)
        : createProblem(response.status, response.statusText);
      throw new ApiError({ ...problem, status: response.status });
    }
    return (await response.json()) as T;
  }
}

interface WirePage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export const api = new ApiClient('/api/v1');

export function apiErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Si è verificato un errore inatteso.';
  const correlationId = error.problem.correlationId;
  return correlationId && correlationId !== 'unavailable'
    ? `${error.message} · ID assistenza: ${correlationId}`
    : error.message;
}
